import React from "react";
import { CheckCircle, Info, AlertTriangle, AlertCircle } from "lucide-react";
import { Typography } from "./Typography";
import { cn } from "@/lib/utils/cn";

export interface InfoBoxProps {
  title?: string;
  message: string | React.ReactNode;
  variant?: "success" | "info" | "warning" | "error";
  showIcon?: boolean;
  className?: string;
}

const variantConfig = {
  success: {
    bg: "bg-green-50",
    border: "border-green-200",
    titleColor: "text-green-900",
    textColor: "text-green-800",
    iconColor: "text-green-600",
    icon: CheckCircle,
  },
  info: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    titleColor: "text-blue-900",
    textColor: "text-blue-800",
    iconColor: "text-blue-600",
    icon: Info,
  },
  warning: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    titleColor: "text-amber-900",
    textColor: "text-amber-800",
    iconColor: "text-amber-600",
    icon: AlertTriangle,
  },
  error: {
    bg: "bg-red-50",
    border: "border-red-200",
    titleColor: "text-red-900",
    textColor: "text-red-800",
    iconColor: "text-red-600",
    icon: AlertCircle,
  },
};

export function InfoBox({
  title,
  message,
  variant = "info",
  showIcon = true,
  className = "",
}: InfoBoxProps) {
  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <div
      className={cn(
        "p-4 rounded-xl border",
        config.bg, config.border,
        className
      )}
      role={variant === "error" ? "alert" : "status"}
    >
      <div className="flex items-start gap-3">
        {showIcon && (
          <Icon
            className={cn("w-5 h-5", config.iconColor, "flex-shrink-0 mt-0.5")}
          />
        )}
        <div className="flex-1">
          {title && (
            <Typography variant="headline5" as="h4" weight="semibold" color={config.titleColor} className="mb-2">
              {title}
            </Typography>
          )}
          <Typography variant="body" as="div" color={config.textColor}>{message}</Typography>
        </div>
      </div>
    </div>
  );
}
